/**
 * Pure adapter functions: FHIR resources → normalized client portal billing types.
 *
 * All functions are defensive — missing fields produce partial but displayable
 * objects rather than throwing. No raw external IDs (Stripe, Candid) are
 * surfaced in the normalized output.
 */
import type {
  ChargeItem,
  Encounter,
  ExplanationOfBenefit,
  Invoice,
  Resource,
} from '@medplum/fhirtypes';

import {
  APPOINTMENT_SERVICE_TYPE_SYSTEM,
  ASSESSMENT_TASK_EXTENSION,
  BILLING_ADJUSTMENT_EXTENSION,
  BILLING_CLIENT_PAID_EXTENSION,
  BILLING_CLIENT_RESPONSIBILITY_EXTENSION,
  BILLING_INSURANCE_PAID_EXTENSION,
  BILLING_INSURANCE_RESPONSIBILITY_EXTENSION,
  BILLING_PAYMENT_STATUS_EXTENSION,
  BILLING_WRITE_OFF_EXTENSION,
  CPT_CODING_SYSTEM,
} from '@unify/fhir-model/constants';

import type {
  ClientPortalBillingServiceLine,
  ClientPortalBillingStatus,
} from './clientPortalFhirTypes';
import { encounterPortalDateRaw } from './portalBillingEncounter';
import { preferredLabelOrFallback } from '../../../lib/loadPractitionerPreferredLabels';
import {
  calculateServiceLineAmountDue,
} from './portalBillingModels';

// ---------------------------------------------------------------------------
// Extension helpers
// ---------------------------------------------------------------------------

function findExtension(resource: Resource, url: string) {
  // @ts-expect-error — FHIR resource may have extension
  const exts: { url?: string; valueInteger?: number; valueString?: string; valueBoolean?: boolean; valueDate?: string }[] =
    (resource as { extension?: unknown[] }).extension ?? [];
  return exts.find((e) => e.url === url);
}

export function extractMoneyFromExtension(resource: Resource, url: string): number | undefined {
  const ext = findExtension(resource, url);
  if (ext?.valueInteger != null) return ext.valueInteger;
  return undefined;
}

export function extractStringFromExtension(resource: Resource, url: string): string | undefined {
  const ext = findExtension(resource, url);
  return ext?.valueString ?? undefined;
}

export function extractBooleanFromExtension(resource: Resource, url: string): boolean | undefined {
  const ext = findExtension(resource, url);
  if (ext?.valueBoolean != null) return ext.valueBoolean;
  return undefined;
}

// ---------------------------------------------------------------------------
// Reference helpers
// ---------------------------------------------------------------------------

/** Safely extracts the ID from a FHIR reference string like "Patient/abc-123". */
export function safeReferenceId(reference: string | undefined, resourceType: string): string | undefined {
  if (!reference) return undefined;
  const prefix = `${resourceType}/`;
  if (reference.startsWith(prefix)) return reference.slice(prefix.length);
  return undefined;
}

// ---------------------------------------------------------------------------
// Billing status inference
// ---------------------------------------------------------------------------

function inferBillingStatus(fhirStatus: string | undefined, extStatus?: string | null): ClientPortalBillingStatus {
  const ext = extStatus?.toLowerCase();
  if (ext === 'overdue') return 'overdue';
  if (ext === 'due') return 'due';
  if (ext === 'paid') return 'paid';
  if (ext === 'partially-paid') return 'partially-paid';
  if (ext === 'processing') return 'processing';
  if (ext === 'void') return 'void';
  if (ext === 'refunded') return 'refunded';
  if (ext === 'insurance-pending' || ext === 'insurance-paid') return 'insurance-pending';

  switch (fhirStatus) {
    case 'issued':
    case 'draft':
      return 'due';
    case 'balanced':
      return 'paid';
    case 'cancelled':
      return 'void';
    default:
      return 'unknown';
  }
}

// ---------------------------------------------------------------------------
// Account → balance
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Coverage → CoverageSummary
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Invoice → ClientPortalInvoiceSummary
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ChargeItem → service line
// ---------------------------------------------------------------------------

export function chargeItemToServiceLine(
  chargeItem: ChargeItem,
  relatedEncounter?: Encounter,
  relatedInvoice?: Invoice,
  practitionerDisplayById: Map<string, string> = new Map(),
): ClientPortalBillingServiceLine {
  const res = chargeItem as Resource;
  const extStatus = extractStringFromExtension(res, BILLING_PAYMENT_STATUS_EXTENSION);
  const status = inferBillingStatus(chargeItem.status, extStatus);

  const dateOfService =
    chargeItem.occurrenceDateTime ??
    (chargeItem.occurrencePeriod as { start?: string } | undefined)?.start ??
    encounterPortalDateRaw(relatedEncounter ?? ({} as Encounter));

  const serviceType =
    (relatedEncounter ? resolveEncounterServiceTypeLabel(relatedEncounter) : undefined) ??
    chargeItem.code?.coding?.[0]?.display ??
    chargeItem.code?.text ??
    'Service';

  const cptCode = chargeItem.code?.coding?.find(
    (c) => c.system === CPT_CODING_SYSTEM || c.system?.includes('cpt'),
  )?.code;

  const performerRef = chargeItem.performer?.[0]?.actor;
  const performerId = safeReferenceId(performerRef?.reference, 'Practitioner');
  const performer = preferredLabelOrFallback(
    performerId,
    practitionerDisplayById,
    performerRef?.display,
  );

  const feeCents = Math.round((chargeItem.priceOverride?.value ?? 0) * 100) || undefined;

  const clientResponsibilityCents = extractMoneyFromExtension(res, BILLING_CLIENT_RESPONSIBILITY_EXTENSION);
  const clientPaidCents = extractMoneyFromExtension(res, BILLING_CLIENT_PAID_EXTENSION) ?? 0;
  const amountDueCents =
    clientResponsibilityCents != null
      ? calculateServiceLineAmountDue({ clientResponsibilityCents, clientPaidCents })
      : undefined;

  const invoiceId = relatedInvoice?.id;
  const encounterId = safeReferenceId(chargeItem.context?.reference, 'Encounter');
  const encounterKind = relatedEncounter
    ? resolveEncounterKind(relatedEncounter)
    : looksLikeAssessmentChargeItem(chargeItem)
      ? 'assessment'
      : 'appointment';

  return {
    id: chargeItem.id ?? 'unknown',
    dateOfService: dateOfService || new Date().toISOString(),
    serviceType,
    provider: performer,
    cptCode,
    feeCents,
    clientResponsibilityCents,
    clientPaidCents,
    amountDueCents,
    insuranceResponsibilityCents: extractMoneyFromExtension(res, BILLING_INSURANCE_RESPONSIBILITY_EXTENSION),
    insurancePaidCents: extractMoneyFromExtension(res, BILLING_INSURANCE_PAID_EXTENSION),
    adjustmentCents: extractMoneyFromExtension(res, BILLING_ADJUSTMENT_EXTENSION),
    writeOffCents: extractMoneyFromExtension(res, BILLING_WRITE_OFF_EXTENSION),
    status,
    invoiceId,
    encounterId,
    chargeItemId: chargeItem.id,
    encounterKind,
  };
}

// ---------------------------------------------------------------------------
// Encounter → fallback service line
// ---------------------------------------------------------------------------

function looksLikeAssessmentChargeItem(chargeItem: ChargeItem): boolean {
  const label =
    chargeItem.code?.coding?.[0]?.display ??
    chargeItem.code?.text ??
    '';
  const t = label.trim().toLowerCase();
  if (!t) return false;
  if (t.includes('assessment')) return true;
  if (/\bphq-?9\b|\bgad-?7\b|\bphq-?a\b|\bscreening\b/.test(t)) return true;
  const hasCpt = chargeItem.code?.coding?.some(
    (c) => c.system === CPT_CODING_SYSTEM || c.system?.includes('cpt'),
  );
  return !hasCpt && t.length > 0;
}

function hasAssessmentServiceTypeCoding(concept: { coding?: { system?: string; code?: string }[] } | undefined): boolean {
  return concept?.coding?.some(
    (c) => c.system === APPOINTMENT_SERVICE_TYPE_SYSTEM && c.code === 'assessment',
  ) ?? false;
}

/** Detects assessment Encounters created by the assessment-encounter-creation bot. */
export function isAssessmentEncounter(encounter: Encounter): boolean {
  if (encounter.extension?.some((e) => e.url === ASSESSMENT_TASK_EXTENSION)) {
    return true;
  }
  if (hasAssessmentServiceTypeCoding(encounter.serviceType)) {
    return true;
  }
  if (encounter.type?.some((t) => hasAssessmentServiceTypeCoding(t))) {
    return true;
  }
  // Assessment encounters link to a Task; session encounters link to Appointment.
  const hasTaskRef = encounter.basedOn?.some((r) => r.reference?.startsWith('Task/'));
  const hasAppointment = (encounter.appointment?.length ?? 0) > 0;
  return hasTaskRef === true && !hasAppointment;
}

export function resolveEncounterKind(encounter: Encounter): 'appointment' | 'assessment' {
  return isAssessmentEncounter(encounter) ? 'assessment' : 'appointment';
}

function codeableConceptLabel(
  concept: { text?: string; coding?: { code?: string; display?: string }[] } | undefined,
): string | undefined {
  const text = concept?.text?.trim();
  // Concepts copied from a ChargeItemDefinition carry the bare CPT/HCPCS code in
  // `text`, which is not a client-facing label — fall through to the descriptor.
  const isBareCode = !!text && (concept?.coding ?? []).some((c) => c.code === text);
  if (text && !isBareCode) return text;
  const display = concept?.coding?.[0]?.display?.trim();
  return display || text || undefined;
}

/** Resolves a client-facing service label from Encounter fields. */
export function resolveEncounterServiceTypeLabel(encounter: Encounter): string {
  const label =
    codeableConceptLabel(encounter.serviceType) ??
    codeableConceptLabel(encounter.type?.[0]);

  if (label) return label;
  if (isAssessmentEncounter(encounter)) return 'Assessment';
  return 'Appointment';
}

// ---------------------------------------------------------------------------
// EOB → billing amounts
// ---------------------------------------------------------------------------

export interface EobBillingAmounts {
  clientResponsibilityCents?: number;
  insurancePaidCents?: number;
  adjustmentCents?: number;
}

export function eobToBillingAmounts(eob: ExplanationOfBenefit): EobBillingAmounts {
  let clientResponsibilityCents: number | undefined;
  let insurancePaidCents: number | undefined;
  let adjustmentCents: number | undefined;

  for (const total of eob.total ?? []) {
    const code = total.category?.coding?.[0]?.code;
    if (!code) continue;
    const cents = Math.round((total.amount?.value ?? 0) * 100);
    if (code === 'paidtoprovider' || code === 'benefit') {
      insurancePaidCents = cents;
    } else if (code === 'deductible' || code === 'coinsurance' || code === 'copay') {
      clientResponsibilityCents = (clientResponsibilityCents ?? 0) + cents;
    } else if (code === 'noncovered' || code === 'discount') {
      adjustmentCents = (adjustmentCents ?? 0) + cents;
    }
  }

  return { clientResponsibilityCents, insurancePaidCents, adjustmentCents };
}

// ---------------------------------------------------------------------------
// DocumentReference → BillingDocument
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Practitioner name helper (re-exported from portalBillingEncounter)
// ---------------------------------------------------------------------------
export { formatPractitionerHumanName } from './portalBillingEncounter';
